import { fetchCFImage } from "@shared/cloudflare.js";
import * as stickerSetFeed from "@shared/stickerSetFeed.js";
import imageStorage from "@shared/ImageStorage.js";
import { Compositor } from "@shared/Compositor.js";
import { Product } from "@shared/Product.js";
import { dlog, debugDesc } from "@shared/utility.js";
import { CartUtilsPrint } from "@shared/CartUtilsPrint.js";

let cachedPreviewCanvas = null;

/**
 * Cart utilities for recipe creation and cart operations
 */
class CartUtils {
    static setPreviewCanvas(element) {
        cachedPreviewCanvas = element || null;
    }

    static getPreviewCanvas() {
        if (cachedPreviewCanvas && cachedPreviewCanvas.isConnected) {
            return cachedPreviewCanvas;
        }

        if (typeof document === "undefined") {
            cachedPreviewCanvas = null;
            return null;
        }

        cachedPreviewCanvas = document.querySelector("cyo-preview-canvas");
        return cachedPreviewCanvas;
    }

    // Re-export print functions for backwards compatibility
    static generatePrintReadyCanvas(cyoDesign, previewCanvasElement = null) {
        return CartUtilsPrint.generatePrintReadyCanvas(cyoDesign, previewCanvasElement);
    }

    static calculateEffectiveUserImageDpi(cyoDesign, previewCanvasElement = null) {
        return CartUtilsPrint.calculateEffectiveUserImageDpi(cyoDesign, previewCanvasElement);
    }

    static fetchFullResolutionImages(cyoDesign) {
        return CartUtilsPrint.fetchFullResolutionImages(cyoDesign);
    }

    static extractTransformsFromPreviewCanvas(previewCanvas = null) {
        return CartUtilsPrint.extractTransformsFromPreviewCanvas(previewCanvas);
    }

    static extractTransformsFromAltCompositor(previewCanvas = null) {
        return CartUtilsPrint.extractTransformsFromAltCompositor(previewCanvas);
    }

    static extractRandomSeedFromPreview(cyoDesign, previewCanvas = null) {
        return CartUtilsPrint.extractRandomSeedFromPreview(cyoDesign, previewCanvas);
    }

    static generateBothModeGripPrintCanvas(cyoDesign, previewCanvasElement = null) {
        return CartUtilsPrint.generateBothModeGripPrintCanvas(cyoDesign, previewCanvasElement);
    }

    static generateGripOnlyPrintCanvas(cyoDesign, previewCanvasElement = null) {
        return CartUtilsPrint.generateGripOnlyPrintCanvas(cyoDesign, previewCanvasElement);
    }

    static async createRecipe(canvas, device, selectedColor = null, selectedColorAlt = null, walletPrintSurface = null, gripCanvas = null) {
        const productConfigStr = device.productConfigString;
        if (!productConfigStr) {
            throw new Error(`Missing productConfigString from ${device.id}`);
        }

        // Add one tag for each selected color. Tags are the colorID prepended with 'cyo4-'.
        var selectByTags = [];
        if (selectedColor && selectedColor.id) {
            selectByTags.push(`cyo4-${selectedColor.id}`);
        }
        if (selectedColorAlt && selectedColorAlt.id) {
            selectByTags.push(`cyo4-${selectedColorAlt.id}`);
        }

        // Build images array based on product category and wallet print surface mode
        let images = [];

        if (device.productCategory === "wallet" && gripCanvas && (walletPrintSurface === "split" || walletPrintSurface === "both")) {
            // Split/both mode: send separate images for wallet and grip surfaces
            const walletBase64 = canvas.toDataURL("image/webp").split(",")[1];
            const gripBase64 = gripCanvas.toDataURL("image/webp").split(",")[1];
            images = [
                {
                    "surface": "walletOnly",
                    "layers": [{ "image": `data:image/webp;base64,${walletBase64}` }]
                },
                {
                    "surface": "gripOnly",
                    "layers": [{ "image": `data:image/webp;base64,${gripBase64}` }]
                }
            ];
        } else {
            // Single surface mode
            const base64Data = canvas.toDataURL("image/webp").split(",")[1];
            let surface;
            switch (device.productCategory) {
                case "case": surface = "auto"; break;
                case "wallet":
                    surface = (walletPrintSurface === "grip") ? "gripOnly" : "walletOnly";
                    break;
                case "grip": surface = "gripOnly"; break;
            }
            images = [
                {
                    "surface": surface,
                    "layers": [{ "image": `data:image/webp;base64,${base64Data}` }]
                }
            ];
        }

        const payload = {
            // NB: Drive says this is a "public" API key and OK to expose.
            "apiKey": "twravuXaWQ60GDtJGr3n5TigSVohRzw8htb71LpFaaPNtffp3uGozquMtX8VRVz5guUw5jriHoJVuolGaTVTDs4qHt6GIzuJ",
            "site": "SFCC,SFCC-US",
            "locale": "en-US",
            "selectedProduct": productConfigStr,
            "selectByTags": selectByTags,
            "images": images
        };

        const url = "https://prd-customizer-int-popsockets-cyo4-func.drivecommerce.com/api/v4/build";
        dlog(`fetching recipe from ${url};`);
        dlog(`payload: ${debugDesc(payload)}`);

        const response = await fetch(url, {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify(payload)
        });

        dlog(`response status: ${response.status}`);

        if (!response.ok) {
            let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
            try {
                const errorBody = await response.text();
                if (errorBody) {
                    errorMessage += ` - ${errorBody}`;
                }
            } catch (e) {
                // Ignore error reading body
            }
            console.error(`Recipe creation failed: ${errorMessage}`);
            throw new Error(errorMessage);
        }

        let result;
        try {
            result = await response.json();
        } catch (error) {
            const errorMessage = `Failed to parse recipe response as JSON: ${error.message}`;
            console.error(errorMessage);
            throw new Error(`Error adding to cart. (1)`);
        }

        dlog(`response: ${debugDesc(result)}`);

        if (!result) {
            const errorMessage = "Recipe response is null or undefined";
            console.error(errorMessage);
            throw new Error(`Error adding to cart. (2)`);
        }

        if (!result.recipe) {
            const errorMessage = "Recipe response missing 'recipe' property";
            console.error(errorMessage);
            throw new Error(`Error adding to cart. (3)`);
        }

        if (!result.recipe.location) {
            const errorMessage = "Recipe response missing 'recipe.location' property";
            console.error(errorMessage);
            throw new Error(`Error adding to cart. (4)`);
        }

        dlog(`location: ${result.recipe.location}`);

        return result.recipe;
    }

    static async createPreviewImageForProductCategory(productCategory, cyoDesign) {
        // Map productCategory to deviceID
        const deviceMapping = {
            "case": "C3PrebuiltMS16ProCase",
            "grip": "C3PrebuiltMagSafeRound",
            "wallet": "C3PrebuiltMagSafeWalletPlusPopTop"
        };

        const deviceID = deviceMapping[productCategory];
        if (!deviceID) {
            throw new Error(`Unknown productCategory: ${productCategory}`);
        }

        // Get the device object
        const device = await import("@shared/deviceFeed.js").then(module => module.deviceWithID(deviceID));
        if (!device) {
            throw new Error(`Device not found for ID: ${deviceID}`);
        }

        // Set up the design options with proper images like generatePrintReadyCanvas does
        const previewCYODesign = cyoDesign.clone();

        // Get sticker images if a sticker set is selected
        if (cyoDesign.stickerSet) {
            previewCYODesign.stickerImages = await stickerSetFeed.imageArrayForSetWithID(cyoDesign.stickerSet.id);
        }

        // Get background image if selected
        if (cyoDesign.galleryImageID) {
            previewCYODesign.backgroundImage = await fetchCFImage(cyoDesign.galleryImageID, 400, "webp", 85, 1);
        } else {
            previewCYODesign.backgroundImage = imageStorage.getBackgroundImage();
        }

        // Get user images from imageStorage
        previewCYODesign.userImage = imageStorage.getUploadedImage();
        previewCYODesign.transparentImage = imageStorage.getTransparentImage();

        // Generate preview composite using Compositor (handles all device asset loading internally)
        const previewCanvas = await Compositor.createCompForCMS(
            device,
            previewCYODesign,
            null, // layerTransformData
            device.imageID, // productImageID
            null // coloredOverlayImageID
        );

        if (!previewCanvas) {
            throw new Error(`Failed to generate preview canvas for ${productCategory}`);
        }

        // Convert canvas to Image object
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => resolve(img);
            img.onerror = () => reject(new Error("Failed to convert canvas to Image"));
            img.src = previewCanvas.toDataURL("image/png");
        });
    }

    /**
     * Fetches and displays recipe assets (PNGs) from a recipe
     *
     * @param {Object} recipe - The recipe object containing a location URL
     * @param {HTMLCanvasElement} printCanvas - Optional canvas that was sent to createRecipe()
     * @param {HTMLCanvasElement} gripPrintCanvas - Optional grip print canvas for grip-only mode
     */
    static async fetchAndDisplayRecipeAssets(recipe, printCanvas = null, gripPrintCanvas = null) {
        const progressDiv = document.createElement("div");
        progressDiv.style.cssText = "position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%); background: white; padding: 30px 40px; border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.3); z-index: 2000; text-align: center;";
        progressDiv.innerHTML = `
            <div style="font-weight: bold; font-size: 16px; margin-bottom: 15px;">Fetching Recipe Assets...</div>
            <div id="recipe-progress-text" style="color: #666; font-size: 14px;">Loading recipe data...</div>
            <div style="margin-top: 15px; width: 200px; height: 4px; background: #e0e0e0; border-radius: 2px; overflow: hidden;">
                <div id="recipe-progress-bar" style="width: 0%; height: 100%; background: #4a90e2; transition: width 0.3s;"></div>
            </div>
        `;
        document.body.appendChild(progressDiv);

        const progressText = document.getElementById("recipe-progress-text");
        const progressBar = document.getElementById("recipe-progress-bar");

        try {
            console.log("Fetching recipe assets...");

            if (!recipe || !recipe.location) {
                throw new Error("No recipe location provided");
            }

            // Get the original design image from the preview canvas
            let originalImageDataUrl = null;
            try {
                const previewCanvas = CartUtils.getPreviewCanvas();
                if (previewCanvas && previewCanvas.getCroppedCanvas) {
                    const croppedCanvas = previewCanvas.getCroppedCanvas();
                    if (croppedCanvas) {
                        originalImageDataUrl = croppedCanvas.toDataURL("image/png");
                    }
                }
            } catch (error) {
                console.warn("Could not get original image from preview canvas:", error);
            }

            // Get the print canvas image that was sent to createRecipe
            let printCanvasDataUrl = null;
            if (printCanvas) {
                try {
                    printCanvasDataUrl = printCanvas.toDataURL("image/png");
                } catch (error) {
                    console.warn("Could not get print canvas image:", error);
                }
            }

            // Get the grip print canvas image if provided (for grip-only mode)
            let gripPrintCanvasDataUrl = null;
            if (gripPrintCanvas) {
                try {
                    gripPrintCanvasDataUrl = gripPrintCanvas.toDataURL("image/png");
                } catch (error) {
                    console.warn("Could not get grip print canvas image:", error);
                }
            }

            console.log("Recipe location:", recipe.location);
            progressText.textContent = "Loading recipe data...";
            progressBar.style.width = "50%";

            // Fetch the recipe JSON from the location
            const recipeResponse = await fetch(recipe.location);
            if (!recipeResponse.ok) {
                throw new Error(`Failed to fetch recipe: ${recipeResponse.status} ${recipeResponse.statusText}`);
            }

            const recipeData = await recipeResponse.json();
            console.log("Recipe data loaded");
            progressText.textContent = "Extracting image URLs...";
            progressBar.style.width = "80%";

            // Extract all PNG URLs that start with "//cz.drrv.co" and end with ".png"
            const pngItems = CartUtils.extractPngUrls(recipeData);
            console.log(`Found ${pngItems.length} PNG URLs`);

            if (pngItems.length === 0) {
                console.warn("No PNG URLs found in recipe");
                document.body.removeChild(progressDiv);
                alert("No PNG images found in recipe");
                return;
            }

            progressText.textContent = "Displaying images...";
            progressBar.style.width = "100%";

            // Display the images at the bottom of the page
            CartUtils.displayRecipeImages(pngItems, originalImageDataUrl, printCanvasDataUrl, gripPrintCanvasDataUrl, recipe.location, recipe.id);

            document.body.removeChild(progressDiv);

        } catch (error) {
            console.error("Error fetching recipe assets:", error);
            document.body.removeChild(progressDiv);
            alert(`Failed to fetch recipe assets: ${error.message}`);
        }
    }

    /**
     * Recursively extracts PNG URLs from recipe JSON
     *
     * @param {*} obj - The object to search (can be string, array, or object)
     * @param {Array} urls - Accumulator array for found URLs
     * @param {Object} parentObj - Parent object to check for 'name' property
     * @returns {Array} Array of objects with {url, name} found in the object
     */
    static extractPngUrls(obj, urls = [], parentObj = null) {
        if (typeof obj === "string") {
            if (obj.startsWith("//cz.drrv.co") && obj.endsWith(".png")) {
                const name = parentObj && parentObj.name ? parentObj.name : null;
                urls.push({ url: "https:" + obj, name });
            }
        } else if (Array.isArray(obj)) {
            obj.forEach(item => CartUtils.extractPngUrls(item, urls, null));
        } else if (obj !== null && typeof obj === "object") {
            Object.values(obj).forEach(value => CartUtils.extractPngUrls(value, urls, obj));
        }
        return urls;
    }

    /**
     * Displays recipe images in a fixed container at the bottom of the page
     *
     * @param {Array} items - Array of {url, name} objects to display
     * @param {string} originalImageDataUrl - Optional data URL of the original design image
     * @param {string} printCanvasDataUrl - Optional data URL of the print canvas sent to createRecipe
     * @param {string} gripPrintCanvasDataUrl - Optional data URL of the grip print canvas for grip-only mode
     * @param {string} recipeLocation - Optional recipe location URL
     * @param {string} recipeId - Optional recipe ID for filename
     */
    static displayRecipeImages(items, originalImageDataUrl = null, printCanvasDataUrl = null, gripPrintCanvasDataUrl = null, recipeLocation = null, recipeId = null) {
        // Remove any existing recipe images container
        const existingContainer = document.getElementById("recipe-images-container");
        if (existingContainer) {
            existingContainer.remove();
        }

        // Create a new container for recipe images
        const container = document.createElement("div");
        container.id = "recipe-images-container";
        container.className = "recipe-images-container";
        container.style.cssText = `
            position: fixed;
            bottom: 0;
            left: 0;
            right: 0;
            background-color: #f5f5f5;
            border-top: 2px solid #333;
            padding: 20px;
            overflow-x: auto;
            z-index: 1000;
            max-height: 300px;
        `;

        const closeButton = document.createElement("button");
        closeButton.textContent = "X";
        closeButton.style.cssText = `
            position: absolute;
            top: 10px;
            left: 10px;
            width: 30px;
            height: 30px;
            background-color: #333;
            color: white;
            border: none;
            border-radius: 4px;
            cursor: pointer;
            font-weight: bold;
            font-size: 16px;
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1001;
        `;
        closeButton.addEventListener("click", () => {
            container.remove();
        });
        container.appendChild(closeButton);

        const totalImages = items.length + (originalImageDataUrl ? 1 : 0) + (printCanvasDataUrl ? 1 : 0) + (gripPrintCanvasDataUrl ? 1 : 0);
        const title = document.createElement("h3");
        title.textContent = `Assets (${totalImages} images)`;
        title.style.cssText = "margin: 0 0 15px 0; text-align: center;";
        container.appendChild(title);

        const imagesWrapper = document.createElement("div");
        imagesWrapper.style.cssText = "display: flex; gap: 10px; overflow-x: auto; padding-bottom: 10px;";

        // Add the original image first if available
        if (originalImageDataUrl) {
            const imgWrapper = document.createElement("div");
            imgWrapper.style.cssText = "flex-shrink: 0; text-align: center;";

            const link = document.createElement("a");
            link.href = originalImageDataUrl;
            const filename = recipeId ? `popsockets-original-design-${recipeId}.png` : "popsockets-original-design.png";
            link.download = filename;
            link.style.cssText = "display: block; cursor: pointer;";

            const img = document.createElement("img");
            img.src = originalImageDataUrl;
            img.alt = "Original design";
            img.id = "scrapedImageOriginal";
            img.style.cssText = "max-height: 200px; border: 1px solid #ccc; background: white;";

            const label = document.createElement("div");
            label.textContent = "Original";
            label.style.cssText = "font-size: 12px; margin-top: 5px; color: #666;";

            link.appendChild(img);
            imgWrapper.appendChild(link);
            imgWrapper.appendChild(label);
            imagesWrapper.appendChild(imgWrapper);
        }

        // Add the print canvas (sent to createRecipe) if available
        if (printCanvasDataUrl) {
            const imgWrapper = document.createElement("div");
            imgWrapper.style.cssText = "flex-shrink: 0; text-align: center;";

            const link = document.createElement("a");
            link.href = printCanvasDataUrl;
            const filename = recipeId ? `popsockets-print-canvas-${recipeId}.png` : "popsockets-print-canvas.png";
            link.download = filename;
            link.style.cssText = "display: block; cursor: pointer;";

            const img = document.createElement("img");
            img.src = printCanvasDataUrl;
            img.alt = "Print canvas sent to createRecipe";
            img.id = "scrapedImagePrintCanvas";
            img.style.cssText = "max-height: 200px; border: 1px solid #ccc; background: white;";

            const label = document.createElement("div");
            label.textContent = "Print Canvas";
            label.style.cssText = "font-size: 12px; margin-top: 5px; color: #666;";

            link.appendChild(img);
            imgWrapper.appendChild(link);
            imgWrapper.appendChild(label);
            imagesWrapper.appendChild(imgWrapper);
        }

        // Add the grip print canvas if available (for grip-only mode)
        if (gripPrintCanvasDataUrl) {
            const imgWrapper = document.createElement("div");
            imgWrapper.style.cssText = "flex-shrink: 0; text-align: center;";

            const link = document.createElement("a");
            link.href = gripPrintCanvasDataUrl;
            const filename = recipeId ? `popsockets-grip-print-${recipeId}.png` : "popsockets-grip-print.png";
            link.download = filename;
            link.style.cssText = "display: block; cursor: pointer;";

            const img = document.createElement("img");
            img.src = gripPrintCanvasDataUrl;
            img.alt = "Grip print canvas";
            img.id = "scrapedImageGripPrintCanvas";
            img.style.cssText = "max-height: 200px; border: 1px solid #ccc; background: white;";

            const label = document.createElement("div");
            label.textContent = "Grip Print Canvas";
            label.style.cssText = "font-size: 12px; margin-top: 5px; color: #666;";

            link.appendChild(img);
            imgWrapper.appendChild(link);
            imgWrapper.appendChild(label);
            imagesWrapper.appendChild(imgWrapper);
        }

        // Add the recipe images
        items.forEach((item, index) => {
            const url = item.url;
            const name = item.name;

            const imgWrapper = document.createElement("div");
            imgWrapper.style.cssText = "flex-shrink: 0; text-align: center;";

            const link = document.createElement("a");
            link.href = url;
            link.target = "_blank";
            link.style.cssText = "display: block; cursor: pointer;";

            const img = document.createElement("img");
            img.src = url;
            img.alt = `Recipe asset ${index + 1}`;
            img.style.cssText = "max-height: 200px; border: 1px solid #ccc; background: white;";

            if (name) {
                img.id = "scrapedImage" + name.replace(/\s+/g, "");
            }

            img.onerror = () => {
                console.error(`Failed to load image: ${url}`);
                img.alt = "Failed to load";
                img.style.border = "1px solid red";
            };

            const label = document.createElement("div");
            label.textContent = name ? name : `Asset ${index + 1}`;
            label.style.cssText = "font-size: 12px; margin-top: 5px; color: #666;";

            link.appendChild(img);
            imgWrapper.appendChild(link);
            imgWrapper.appendChild(label);
            imagesWrapper.appendChild(imgWrapper);
        });

        container.appendChild(imagesWrapper);

        // Add link to full recipe if location is provided
        if (recipeLocation) {
            const recipeLink = document.createElement("a");
            const fullRecipeUrl = recipeLocation.startsWith("//") ? "https:" + recipeLocation : recipeLocation;
            recipeLink.href = fullRecipeUrl;
            recipeLink.target = "_blank";
            recipeLink.textContent = "View Full Recipe";
            recipeLink.style.cssText = `
                display: block;
                text-align: center;
                margin-top: 15px;
                color: #4a90e2;
                text-decoration: underline;
                font-size: 14px;
                cursor: pointer;
            `;
            container.appendChild(recipeLink);
        }

        document.body.appendChild(container);

        console.log("Recipe images displayed");
    }
}

export { CartUtils };
